import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Wizard } from 'components/wizard';
import { Breadcrumbs } from 'components/navigation';
import { Intent } from '@blueprintjs/core';
import { HelperFunctions } from 'helpers';
import { useNavigate } from 'react-router-dom';
import { AlertUnsavedData, NotificationToaster } from 'components/notifications';
import { useDirtyData } from 'hooks/useDirtyData';
import { PageRestricted } from 'pages/errors/page-restricted';
import { UserAccess } from 'components/user-access';
import { DocumentsService } from 'services/DocumentsService';
import { DocumentTypeDetails } from 'pages/documents/settings/document-types/shared/document-type-details';
import { DocumentTypeAdditionalDetails } from 'pages/documents/settings/document-types/shared/document-type-additional-settings/DocumentTypeAdditionalSettings';
import { OrganisationDocuments } from 'pages/documents/settings/document-types/shared/organisation-documents';

export function NewDocumentType(props) {
    const defaultDocumentType = {
        name: "",
        area: null,
        dataRetention: null
    }

    const [documentType, setDocumentType] = useState({ ...defaultDocumentType });
    const [originalDocumentType, setOriginalDocumentType] = useState({ ...defaultDocumentType });

    const [documentOptions, setDocumentOptions] = useState({
        documentAreas: []
    });

    const [createdId, setCreatedId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [wizardStep, setWizardStep] = useState(0);
    const [wizardValid, setWizardValid] = useState([false, false]);
    const [organisationDocuments, setOrganisationDocuments] = useState([]);

    const isDocumentTypeDirty = useDirtyData(originalDocumentType, documentType);

    var navigate = useNavigate();

    const organisationDocumentType = documentType.area && documentType.area === 'Organisation';

    useEffect(() => {
        DocumentsService.getDocumentOptions()
            .then((response) => {
                setDocumentOptions(response);
                setLoading(false);
                setOriginalDocumentType(defaultDocumentType);
            })
            .catch(() => {
                NotificationToaster.show(Intent.DANGER, "Could not load document options. Please try again.");
            })
    }, []);

    const onWizardChange = (step, direction, previousStep) => {
        setWizardStep(step);
    }

    const onWizardStepValidationChange = (stepIndex, isValid) => {
        const validationClone = HelperFunctions.deepClone(wizardValid);
        validationClone[stepIndex] = isValid;
        setWizardValid(validationClone);
    }

    const addDocumentType = () => {
        setSaving(true);

        if (wizardValid.some(x => !x)) {
            NotificationToaster.show(Intent.WARNING, "There are errors on the page that need your attention");
            setSaving(false);
            return;
        }

        DocumentsService.createDocumentType(documentType).then(
            (newId) => {
                setSaving(false);
                setCreatedId(newId);
                if (organisationDocuments.length > 0 && organisationDocumentType) {
                    uploadDocuments(newId).then((results) => {
                        const anyFailed = results.some(r => r === false);
                        if (anyFailed) {
                            NotificationToaster.show(Intent.WARNING, "Document type created but some documents failed to upload.");
                            navigateToDocumentTypeDetails(newId);
                        } else {
                            NotificationToaster.show(Intent.SUCCESS, "Document type created and all documents uploaded successfully.");
                            navigateToDocumentTypeDetails(newId);
                        }
                    });
                } else {
                    NotificationToaster.show(Intent.SUCCESS, "Document type created successfully.");
                    navigateToDocumentTypeDetails(newId);
                }
            },
            (err) => {
                NotificationToaster.show(Intent.DANGER, err);
                setSaving(false);
            }
        );
    }

    const navigateToDocumentTypeDetails = (id) => {
        // Defer so React commits the setCreatedId render and AlertUnsavedData
        // clears its blocker ref before navigation fires.
        setTimeout(() => navigate(`/document-settings/types/${id}`), 0);
    }

    const onOrganisationDocumentRemove = (fileName) => {
        const updatedFiles = organisationDocuments.filter(x => x.name !== fileName);
        setOrganisationDocuments(updatedFiles);
    }

    const uploadDocuments = (newId) => {
        const promises = [];

        setSaving(true);
        organisationDocuments.forEach(async (fileData) => {
            promises.push(DocumentsService.uploadDocument(fileData, '', newId).then(() => {
                return true
            }, () => {
                return false;
            }));
        });

        return Promise.all(promises);
    }

    return <UserAccess perform={['Documents:Manage']}
        yes={() => (<div className="row">
            <Breadcrumbs items={props.breadcrumbs} />
            <h1>New Document Type</h1>
            <div className="spacer-bottom">
                <p>Here you can create a category for documents to be stored against, for types created under the Asset and Employee areas, you will need to upload the files in the individual employee or asset profiles. For types created under the Organisation area, it is possible to upload a document instantly or in the All Documents screen.</p>
            </div>
            <Wizard
                id="new-document-type-wizard"
                onStepChange={onWizardChange}
                onFinish={addDocumentType}
                canProceed={wizardValid[wizardStep]}
                disabled={saving}
                alignButtonsRight={true}
                finishButtonText="Create Document Type"
                footer={() => null}
                allowBack={true}
                canFinish={true}>
                <DocumentTypeDetails
                    documentType={documentType}
                    onChange={setDocumentType}
                    documentAreas={documentOptions.documentAreas}
                    onValidationChange={(isValid) => onWizardStepValidationChange(0, isValid)}
                    saving={saving}
                    loading={loading}
                />
                <DocumentTypeAdditionalDetails
                    documentType={documentType}
                    onChange={setDocumentType}
                    onValidationChange={(isValid) => onWizardStepValidationChange(1, isValid)}
                    saving={saving}
                    loading={loading}
                />
                {organisationDocumentType && (<OrganisationDocuments
                    newDocuments={organisationDocuments}
                    onUpload={(files) => setOrganisationDocuments([...organisationDocuments, ...files])}
                    saving={saving}
                    loading={loading}
                    onRemove={onOrganisationDocumentRemove}
                />)}
            </Wizard>

            <AlertUnsavedData
                isDirty={isDocumentTypeDirty && !createdId}>
            </AlertUnsavedData>
        </div>)}
        no={() => (
            <PageRestricted />
        )}
    />
}

NewDocumentType.propTypes = {
    breadcrumbs: PropTypes.array
}; 